import { useState, useEffect, useRef, useMemo } from 'react'
import './App.css'
import Canvas from './components/Canvas'
import Menu from './components/Menu'

/* ── Tiny grain noise generator (offscreen canvas) ── */
function useGrainUrl(opacity) {
  return useMemo(() => {
    if (!opacity || opacity <= 0) return ''
    const size = 150
    const c = document.createElement('canvas')
    c.width = size; c.height = size
    const ctx = c.getContext('2d')
    const img = ctx.createImageData(size, size)
    for (let i = 0; i < img.data.length; i += 4) {
      const v = Math.random() * 255
      img.data[i] = v; img.data[i+1] = v; img.data[i+2] = v
      img.data[i+3] = Math.round(opacity * 255)
    }
    ctx.putImageData(img, 0, 0)
    return c.toDataURL()
  }, [opacity])
}

function App() {
  const [menuOpen, setMenuOpen] = useState(false)
  const [config, setConfig] = useState({
    paused: false,
    window: {
      width: 92,
      height: 46,
      borderRadius: 18
    },
    background: {
      speed: 0.5,
      direction: 1,
      image: null,
      offset: 0,
      motionBlur: 0
    },
    foreground: {
      speed: 1.5,
      direction: 1,
      image: null,
      offset: 0,
      motionBlur: 0
    },
    stamps: [],
    export: {
      duration: 10,
      quality: '1080p',
      fps: 30
    },
    siteBg: {
      mode: 'solid',
      color: '#0e0e0e',
      accentColor: '#222222',
      dotSize: 1,
      dotSpacing: 24,
      grainOpacity: 0.08
    }
  })

  // Toggle menu with 'm' key
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'm' || e.key === 'M') {
        if (['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement.tagName)) return
        setMenuOpen(prev => !prev)
      }
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [])

  // Auto-load default images on startup
  useEffect(() => {
    const loadImage = (src) => new Promise((resolve) => {
      const img = new Image()
      img.onload = () => resolve(img)
      img.onerror = () => resolve(null)
      img.src = src
    })

    Promise.all([
      loadImage('/image/Panorama ciel.jpg'),
      loadImage('/image/first-plan-transparent.png')
    ]).then(([bgImg, fgImg]) => {
      setConfig(prev => ({
        ...prev,
        background: { ...prev.background, image: bgImg },
        foreground: { ...prev.foreground, image: fgImg }
      }))
    })
  }, [])

  const updateConfig = (section, updates) => {
    if (section === 'root') {
      setConfig(prev => ({ ...prev, ...updates }))
    } else if (section === 'stamps') {
      setConfig(prev => ({ ...prev, stamps: updates }))
    } else {
      setConfig(prev => ({
        ...prev,
        [section]: { ...prev[section], ...updates }
      }))
    }
  }

  /* ── Compute background CSS ── */
  const siteBg = config.siteBg
  const grainUrl = useGrainUrl(siteBg.mode === 'grain' ? siteBg.grainOpacity : 0)

  const bgStyle = useMemo(() => {
    const s = { backgroundColor: siteBg.color }
    if (siteBg.mode === 'dots') {
      const sz = siteBg.dotSize || 1
      const sp = siteBg.dotSpacing || 24
      const c  = siteBg.accentColor || '#333'
      s.backgroundImage = `radial-gradient(circle, ${c} ${sz}px, transparent ${sz}px)`
      s.backgroundSize = `${sp}px ${sp}px`
    } else if (siteBg.mode === 'grid') {
      const sz = siteBg.dotSize || 1
      const sp = siteBg.dotSpacing || 24
      const c  = siteBg.accentColor || '#222'
      s.backgroundImage = `linear-gradient(${c} ${sz}px, transparent ${sz}px), linear-gradient(90deg, ${c} ${sz}px, transparent ${sz}px)`
      s.backgroundSize = `${sp}px ${sp}px`
    } else if (siteBg.mode === 'grain' && grainUrl) {
      s.backgroundImage = `url(${grainUrl})`
      s.backgroundRepeat = 'repeat'
    }
    return s
  }, [siteBg.mode, siteBg.color, siteBg.accentColor, siteBg.dotSize, siteBg.dotSpacing, siteBg.grainOpacity, grainUrl])

  return (
    <div className="app" style={bgStyle}>
      <button
        className={`menu-toggle ${menuOpen ? 'shifted' : ''}`}
        onClick={() => setMenuOpen(!menuOpen)}
        aria-label="Toggle menu"
      />

      <Menu
        open={menuOpen}
        config={config}
        updateConfig={updateConfig}
      />

      <Canvas config={config} />
    </div>
  )
}

export default App
